import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button, Card, ConfirmDialog, Pill } from '@/shared/components';
import { toApiError, useApi } from '@/shared/api';
import { userKeys } from './queries';

const policyKey = ['workspace', 'two-factor-policy'] as const;

export function WorkspaceTwoFactorPolicyCard() {
  const api = useApi();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const policy = useQuery({ queryKey: policyKey, queryFn: api.workspaceTwoFactorPolicy.get });
  const update = useMutation({
    mutationFn: api.workspaceTwoFactorPolicy.update,
    onSuccess: async (next) => {
      queryClient.setQueryData(policyKey, next);
      await queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      setConfirmOpen(false);
      toast.success(
        next.required ? 'Zwei-Faktor-Pflicht aktiviert' : 'Zwei-Faktor-Pflicht aufgehoben',
      );
    },
    onError: async (error) => {
      const apiError = await toApiError(error);
      toast.error(apiError.detail ?? apiError.title);
    },
  });

  if (policy.isLoading) {
    return (
      <Card className="text-ink-soft mb-6 p-5 text-sm">
        Die Zwei-Faktor-Richtlinie wird geladen…
      </Card>
    );
  }
  if (policy.error || !policy.data) {
    return (
      <Card className="text-sealwax mb-6 p-5 text-sm">
        Die Zwei-Faktor-Richtlinie konnte nicht geladen werden.
      </Card>
    );
  }

  return (
    <>
      <Card className="mb-6 p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <h2 className="text-ink font-serif text-xl">Zwei-Faktor-Richtlinie</h2>
              <Pill variant={policy.data.required ? 'live' : 'outline'} size="sm">
                {policy.data.required ? 'Verpflichtend' : 'Optional'}
              </Pill>
            </div>
            <p className="text-ink-soft max-w-2xl text-sm">
              Ohne Pflicht richtet jeder Benutzer die Zwei-Faktor-Anmeldung selbst ein. Mit Pflicht
              wird jeder, der sie noch nicht hat, nach der Passworteingabe durch die Einrichtung
              geführt.
            </p>
          </div>
          <Button
            variant={policy.data.required ? 'ghost' : 'brand'}
            className="min-h-11"
            onClick={() =>
              policy.data.required ? update.mutate({ required: false }) : setConfirmOpen(true)
            }
            loading={update.isPending}
          >
            {policy.data.required ? 'Pflicht aufheben' : 'Zwei-Faktor verpflichtend machen'}
          </Button>
        </div>
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Zwei-Faktor-Anmeldung verpflichtend machen?"
        description="Alle laufenden Sitzungen dieses Workspaces werden beendet; bestehende Zugriffe gelten höchstens noch 15 Minuten. Danach führt die Anmeldung jeden ohne zweiten Faktor durch die Einrichtung."
        confirmLabel="Jetzt aktivieren"
        variant="brand"
        loading={update.isPending}
        onConfirm={() => update.mutate({ required: true })}
      />
    </>
  );
}
